import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { ROUTE_BUILDERS } from '@/config/routes'
import { COMPANY_ROLES } from '@/constants/roles'
import { useUserRole } from '@/hooks/useUserRole'
import { showApiError } from '@/lib/utils/notifications'
import { getApiLogDetail } from '../services/reportsService'
import type { ApiLogDetail } from '../types/api'
import { formatApiLogBody, formatApiLogResponseBody } from '../utils/apiLogBody'

function padApplicantId(id: number | undefined): string {
  if (id == null) return ''
  return String(id).padStart(8, '0')
}

function formatCreatedAt(value: string | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    })
  }
  return String(value)
}

function DetailField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <div className="text-sm text-slate-800">{children}</div>
    </div>
  )
}

export function ApiLogDetailContent({
  logId,
  linkApplication = true,
  framed = true,
}: {
  logId: string
  /** Link the application id. Off when the popup is already on that application. */
  linkApplication?: boolean
  /** Card chrome for the full page. The popup supplies its own frame. */
  framed?: boolean
}) {
  const { roleName, hasWriteAccess } = useUserRole()
  const isCompanyRole = (COMPANY_ROLES as readonly string[]).includes(roleName)
  const canOpenApplication = linkApplication && hasWriteAccess('application') && !isCompanyRole

  const [detail, setDetail] = useState<ApiLogDetail | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!logId) return
    let cancelled = false
    setLoading(true)
    setDetail(null)
    void getApiLogDetail(logId)
      .then((data) => {
        if (!cancelled) setDetail(data)
      })
      .catch((error) => {
        if (!cancelled) showApiError(error)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [logId])

  const formattedRequest = useMemo(() => formatApiLogBody(detail?.request_body), [detail])
  const formattedResponse = useMemo(
    () => formatApiLogResponseBody(detail?.api_type, detail?.response_body),
    [detail],
  )

  if (loading) {
    return (
      <div className="flex min-h-[12rem] items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-[rgb(21_151_255)]" aria-hidden />
      </div>
    )
  }

  if (!detail) {
    return <p className="text-center text-slate-500">Log not found.</p>
  }

  const fields = (
    <>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <DetailField label="Log ID">{detail.api_log_id ?? '—'}</DetailField>
        <DetailField label="Application ID">
          {detail.fk_applicant_id != null ? (
            canOpenApplication ? (
              <Link
                to={ROUTE_BUILDERS.DASHBOARD_APPLICATION_DETAIL(detail.fk_applicant_id)}
                className="text-[#007BFF] hover:underline"
              >
                {padApplicantId(detail.fk_applicant_id)}
              </Link>
            ) : (
              padApplicantId(detail.fk_applicant_id)
            )
          ) : (
            '—'
          )}
        </DetailField>
        <DetailField label="Api Type">{detail.api_type ?? '—'}</DetailField>
        <DetailField label="Api Method">{detail.api_method ?? '—'}</DetailField>
        <DetailField label="Created At">{formatCreatedAt(detail.created_at)}</DetailField>
      </div>
      <DetailField label="Location">
        <p className="whitespace-pre-wrap break-words">{detail.location ?? '—'}</p>
      </DetailField>
      <DetailField label="Request Body">
        <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-slate-50 p-3 text-xs">
          {formattedRequest}
        </pre>
      </DetailField>
      <DetailField label="Response Body">
        <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-slate-50 p-3 text-xs">
          {formattedResponse}
        </pre>
      </DetailField>
    </>
  )

  if (!framed) {
    return <div className="space-y-6">{fields}</div>
  }

  return <div className="space-y-6 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">{fields}</div>
}
